import React, { useRef, useEffect } from "react";
import styled from "styled-components";
import { Menu } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import { useResponsiveLayout } from "../../contexts/ResponsiveLayoutContext";
import { useThemeMode } from "../../contexts/ThemeContext";

interface ButtonProps {
  $themeMode: 'light' | 'dark';
}

const Button = styled(IconButton)<ButtonProps>`
  position: fixed;
  left: 16px;
  top: 16px;
  z-index: 1201;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  background-color: ${({ $themeMode }) => $themeMode === 'light' ? '#FFFFFF' : '#1E1E1E'} !important;
  cursor: pointer !important;
  touch-action: manipulation;
  user-select: none;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0.1);
  pointer-events: auto !important;
  
  &:hover {
    background-color: ${({ $themeMode }) => $themeMode === 'light' ? '#F5F5F5' : '#2A2A2A'} !important;
  }
  
  &:active {
    background-color: ${({ $themeMode }) => $themeMode === 'light' ? '#E0E0E0' : '#353535'} !important;
  }
`;

const SidebarToggleButton = () => {
  const { mode } = useThemeMode();
  const { isMobile, toggleSidebar } = useResponsiveLayout();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const touchHandled = useRef(false);

  useEffect(() => {
    const button = buttonRef.current;
    if (!button) return;

    const handleTouchStart = (e: TouchEvent) => {
      if (!isMobile) return;
      e.preventDefault();
      e.stopPropagation();
      touchHandled.current = true;
      toggleSidebar();
      setTimeout(() => {
        touchHandled.current = false;
      }, 400);
    };

    const handleClick = (e: MouseEvent) => {
      if (touchHandled.current) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      toggleSidebar();
    };

    button.addEventListener('touchstart', handleTouchStart, { passive: false, capture: true });
    button.addEventListener('click', handleClick, { capture: true });

    return () => {
      button.removeEventListener('touchstart', handleTouchStart, { capture: true } as any);
      button.removeEventListener('click', handleClick, { capture: true });
    };
  }, [isMobile, toggleSidebar]);

  return (
    <Button 
      ref={buttonRef}
      $themeMode={mode}
      aria-label="Відкрити меню"
    >
      <Menu sx={{ color: mode === 'light' ? '#424242' : '#5073F8', pointerEvents: 'none' }} />
    </Button>
  );
};

export default SidebarToggleButton;
